import { type AxiosInstance, type AxiosRequestConfig } from 'axios'
import { ENDPOINTS } from 'config'

import {
  type AssetType,
  type DeleteAssetResponse,
  type RenameAssetResponse,
  type UploadAssetResponse
} from 'utils/hooks/types'
import { type ILinkAccount, type ICampaign } from './apiTypes'
import { axios } from './axiosInstance'

class RestAPIs {
  private readonly requestInstance: AxiosInstance

  constructor(axiosInstance: AxiosInstance) {
    this.requestInstance = axiosInstance
  }

  private getHeader(token: string): AxiosRequestConfig {
    return {
      headers: { Authorization: `Bearer ${token}` }
    }
  }

  getUser = async (param: any, accessToken: string): Promise<any> =>
    await this.requestInstance.post(
      ENDPOINTS.SEARCH_USER,
      param,
      this.getHeader(accessToken)
    )

  getUserAsAdmin = async (param: any, accessToken: string): Promise<any> =>
    await this.requestInstance.post(
      ENDPOINTS.ADMIN_USER,
      param,
      this.getHeader(accessToken)
    )

  updateEmail = async (param: any, accessToken: string): Promise<any> =>
    await this.requestInstance.post(
      ENDPOINTS.ADMIN_UPDATE_EMAIL,
      param,
      this.getHeader(accessToken)
    )

  listAssets = async (accessToken: string): Promise<AssetType[]> =>
    await this.requestInstance.get(
      ENDPOINTS.ASSETS,
      this.getHeader(accessToken)
    )

  deleteAssets = async (
    key: string,
    accessToken: string
  ): Promise<DeleteAssetResponse> =>
    await this.requestInstance.delete(
      `${ENDPOINTS.ASSETS}/${key}`,
      this.getHeader(accessToken)
    )

  renameAssets = async (
    oldName: string,
    newName: string,
    accessToken: string
  ): Promise<RenameAssetResponse> =>
    await this.requestInstance.put(
      `${ENDPOINTS.ASSETS}/${oldName}/${newName}`,
      {},
      this.getHeader(accessToken)
    )

  uploadAssets = async (
    formData: FormData,
    accessToken: string
  ): Promise<UploadAssetResponse> =>
    await this.requestInstance.post(ENDPOINTS.ASSETS, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        Authorization: `Bearer ${accessToken}`
      }
    })

  getCampaigns = async (accessToken: string): Promise<any> =>
    await this.requestInstance.get(
      ENDPOINTS.CAMPAIGNS,
      this.getHeader(accessToken)
    )

  getCampaignById = async (id: string, accessToken: string): Promise<any> =>
    await this.requestInstance.get(
      `${ENDPOINTS.CAMPAIGNS}/${id}`,
      this.getHeader(accessToken)
    )

  createCampaign = async (
    param: ICampaign,
    accessToken: string
  ): Promise<any> =>
    await this.requestInstance.post(
      ENDPOINTS.CAMPAIGNS,
      param,
      this.getHeader(accessToken)
    )

  editCampaign = async (
    campaignId: string,
    param: ICampaign,
    accessToken: string
  ): Promise<any> =>
    await this.requestInstance.put(
      `${ENDPOINTS.CAMPAIGNS}/${campaignId}`,
      param,
      this.getHeader(accessToken)
    )

  sendCampaign = async (param: any, accessToken: string): Promise<any> =>
    await this.requestInstance.post(
      `${ENDPOINTS.CAMPAIGNS}/send`,
      param,
      this.getHeader(accessToken)
    )

  deleteCampaign = async (
    campaignId: string,
    accessToken: string
  ): Promise<any> =>
    await this.requestInstance.delete(
      `${ENDPOINTS.CAMPAIGNS}/${campaignId}`,
      this.getHeader(accessToken)
    )

  getCampaignLogsById = async (
    campaignId: string,
    startDate: string,
    endDate: string,
    accessToken: string
  ): Promise<any> =>
    await this.requestInstance.get(
      `${ENDPOINTS.CAMPAIGN_LOGS}/${campaignId}?startDate=${startDate}&endDate=${endDate}`,
      this.getHeader(accessToken)
    )

  getSettings = async (accessToken: string): Promise<any> =>
    await this.requestInstance.get(
      ENDPOINTS.SETTINGS,
      this.getHeader(accessToken)
    )

  getICStatus = async (email: string, accessToken: string): Promise<any> =>
    await this.requestInstance.get(
      `${ENDPOINTS.IC_STATUS}?email=${email}`,
      this.getHeader(accessToken)
    )

  getICAccounts = async (email: string, accessToken: string): Promise<any> =>
    await this.requestInstance.get(
      `${ENDPOINTS.IC_ACCOUNTS}?email=${email}`,
      this.getHeader(accessToken)
    )

  registerICAccount = async (param: any, accessToken: string): Promise<any> =>
    await this.requestInstance.post(
      `${ENDPOINTS.IC_REGISTER_ACCOUNT}`,
      param,
      this.getHeader(accessToken)
    )

  updateICEmail = async (param: any, accessToken: string): Promise<any> =>
    await this.requestInstance.post(
      `${ENDPOINTS.IC_UPDATE_EMAIL}`,
      param,
      this.getHeader(accessToken)
    )

  unregisterICAccount = async (param: any, accessToken: string): Promise<any> =>
    await this.requestInstance.post(
      `${ENDPOINTS.IC_UNREGISTER_ACCOUNT}`,
      param,
      this.getHeader(accessToken)
    )

  changePasswordICAccount = async (
    param: any,
    accessToken: string
  ): Promise<any> =>
    await this.requestInstance.post(
      `${ENDPOINTS.IC_PASSWORD}`,
      param,
      this.getHeader(accessToken)
    )

  linkICAccounts = async (param: any, accessToken: string): Promise<any> =>
    await this.requestInstance.post(
      `${ENDPOINTS.IC_LINK_ACCOUNTS}`,
      param,
      this.getHeader(accessToken)
    )

  unlinkICAccounts = async (param: any, accessToken: string): Promise<any> =>
    await this.requestInstance.post(
      `${ENDPOINTS.IC_UNLINK_ACCOUNTS}`,
      param,
      this.getHeader(accessToken)
    )

  getCCSAccount = async (param: any, accessToken: string): Promise<any> =>
    await this.requestInstance.post(
      `${ENDPOINTS.ACCOUNT}`,
      param,
      this.getHeader(accessToken)
    )

  manualConfirmUser = async (param: any, accessToken: string): Promise<any> =>
    await this.requestInstance.post(
      `${ENDPOINTS.ADMIN_CONFIRM}`,
      param,
      this.getHeader(accessToken)
    )

  syncPassword = async (param: any, accessToken: string): Promise<any> =>
    await this.requestInstance.post(
      `${ENDPOINTS.IC_SYNC_PASSWORD}`,
      param,
      this.getHeader(accessToken)
    )

  updateMailingAddress = async (
    param: any,
    accessToken: string
  ): Promise<any> => {
    await this.requestInstance.put(
      `${ENDPOINTS.UPDATE_MAILING_ADDRESS}`,
      param,
      this.getHeader(accessToken)
    )
  }

  updateInfo = async (param: any, accessToken: string): Promise<any> => {
    return await this.requestInstance.post(
      `${ENDPOINTS.UPDATE_INFO}`,
      param,
      this.getHeader(accessToken)
    )
  }

  unregisterAccount = async (param: any, accessToken: string): Promise<any> => {
    await this.requestInstance.post(
      `${ENDPOINTS.UNREGISTER_ACCOUNT}`,
      param,
      this.getHeader(accessToken)
    )
  }

  linkAccount = async (param: any, accessToken: string): Promise<any> => {
    await this.requestInstance.post(
      `${ENDPOINTS.LINK_ACCOUNT}`,
      param,
      this.getHeader(accessToken)
    )
  }

  verifyAccount = async (param: any): Promise<any> =>
    await this.requestInstance.post(
      ENDPOINTS.VERIFY_ACCOUNT,
      param,
      this.getHeader(param.AccessToken)
    )

  verifyAccountToLink = async (param: any): Promise<any> =>
    await this.requestInstance.post(
      ENDPOINTS.VERIFY_ACCOUNT_TO_LINK,
      param,
      this.getHeader(param.AccessToken)
    )

  icLinkAccount = async (param: ILinkAccount): Promise<any> => {
    return await this.requestInstance.post(ENDPOINTS.IC_LINK_ACCOUNT, param,
      this.getHeader(param.AccessToken))
  }

  updateEmailAfterLink = async ({ accountId, accessToken }: { accountId: string, accessToken: string }): Promise<any> =>
    await this.requestInstance.post(
      ENDPOINTS.UPDATE_EMAIL_AFTER_LINK,
      { accountId },
      this.getHeader(accessToken)
    )

  getAllLinkedAccounts = async (param: any, accessToken: string): Promise<any> =>
    await this.requestInstance.post(
      ENDPOINTS.ALL_LINKED_ACCOUNTS,
      param,
      this.getHeader(accessToken)
    )

  updateCognitoAttribute = async (param: any, accessToken: string): Promise<any> =>
    await this.requestInstance.post(
      ENDPOINTS.UPDATE_COGNITO_ATTRIBUTE,
      param,
      this.getHeader(accessToken)
    )
}

export const APIs = new RestAPIs(axios)
